import { Method, UpsertVmsPayload } from '_lib/api';
import fetchAPI from '_lib/api/fetchWrapper';
import apiRoute from '_lib/api/paths';
import { ToastTitle } from '_store/application/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { createSliceSaga, SagaType } from 'redux-toolkit-saga';
import { delay, put, select } from 'typed-redux-saga';

import { pmvL10nArgs } from '../application/pmvLabels';
import applicationReducer from '../application/reducer';
import managePmvFormReducer from '../forms/manage-pmv';
import pmvsReducer from './pmvsReducers';
import { allVmsSelector } from './selectors';
import {
  collectVmsWorkingStatusAlerts,
  normalizeVmsIvimResponse,
  normalizeVmsList,
} from './utils';

const VMS_STATUS_POLL_INTERVAL_MS = 60_000;

export function* getAllVms() {
  try {
    const response = yield fetchAPI(apiRoute.getVms);
    yield* put(pmvsReducer.actions.vmsResponse(normalizeVmsList(response)));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('ERROR getAllVms >>>>>>> ', e);
    yield* put(pmvsReducer.actions.vmsResponse([]));
  }
}

/** Poll VMS list every minute; alert on working-status changes; refresh markers silently. */
export function* pollVmsStatus() {
  while (true) {
    yield* delay(VMS_STATUS_POLL_INTERVAL_MS);
    try {
      const previous = yield* select(allVmsSelector);
      const response = yield fetchAPI(apiRoute.getVms);
      const next = normalizeVmsList(response);

      const alerts = collectVmsWorkingStatusAlerts(previous, next);
      if (alerts.length > 0) {
        yield* put(pmvsReducer.actions.vmsStatusAlertsAdded(alerts));
      }

      yield* put(pmvsReducer.actions.vmsSilentResponse(next));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('ERROR pollVmsStatus >>>>>>> ', e);
    }
  }
}

export function* upsertVms(
  action: PayloadAction<{ id: string; vmsPayload: UpsertVmsPayload; isCreate?: boolean }>,
) {
  let toastContent: ToastTitle;
  const { id, vmsPayload, isCreate } = action.payload;
  const requestBody: UpsertVmsPayload = {
    name: vmsPayload.name.trim(),
    rsus: [...vmsPayload.rsus],
  };

  try {
    yield fetchAPI(apiRoute.updateVms(id), {
      method: Method.PUT,
      authenticated: true,
      params: requestBody,
    });

    toastContent = isCreate
      ? { id: 'forms-pmv-creation-success', args: pmvL10nArgs() }
      : { id: 'forms-pmv-update-success', args: pmvL10nArgs() };
    yield* put(managePmvFormReducer.actions.resetForm());
    yield* put(applicationReducer.actions.dialogClosed());
    yield* getAllVms();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('ERROR upsertVms >>>>>>> ', e);
    toastContent = {
      id: isCreate ? 'forms-pmv-creation-fail' : 'forms-pmv-update-fail',
      args: pmvL10nArgs({ error: e?.packet?.message ?? 'Unknown error' }),
    };
  } finally {
    yield put(applicationReducer.actions.toastAdded(toastContent));
  }
}

export function* deleteVms(action: PayloadAction<string>) {
  yield* put(applicationReducer.actions.dialogRequestStarted());

  try {
    const id = action.payload;
    yield fetchAPI(apiRoute.deleteVms(id), { method: Method.DELETE });

    yield* put(applicationReducer.actions.dialogRequestFinished({ ok: true }));
    yield* getAllVms();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('Error deleteVms >>>>>>>', e);
  }
}

export function* getVmsIvim(action: PayloadAction<string>) {
  const vmsId = action.payload;
  yield* put(pmvsReducer.actions.vmsIvimRequest(vmsId));

  try {
    const response = yield fetchAPI(apiRoute.getVmsIvim(vmsId));
    const ivims = normalizeVmsIvimResponse(response);
    yield* put(pmvsReducer.actions.vmsIvimResponse({ vmsId, ivims }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log('ERROR getVmsIvim >>>>>>> ', e);
    yield* put(pmvsReducer.actions.vmsIvimError(vmsId));
    yield* put(pmvsReducer.actions.vmsIvimResponse({ vmsId, ivims: [] }));
  }
}

export const pmvsSagas = createSliceSaga({
  name: 'pmvs',
  caseSagas: {
    vmsRequest: getAllVms,
    upsertVms,
    deleteVms,
    getVmsIvim,
  },
  sagaType: SagaType.Watch,
});
